import React, { FC, SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import { Tab, Tabs } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import type { TFunction } from "i18next";
import { myTimesheetTabsState } from "my-timesheet/states";

interface UseMyTimesheetTabsStateParams {
  id: string;
  tabsDef: string[];
}

interface UseMyTimesheetTabsStateResult {
  activeTab: number;
  handleTabChange: (event: SyntheticEvent, newValue: number) => void;
  MySheetOcTabs: FC;
}

export const useMyTimesheetTabsState = ({
  id,
  tabsDef,
}: UseMyTimesheetTabsStateParams): UseMyTimesheetTabsStateResult => {
  const { t }: { t: TFunction } = useTranslation();
  const [activeTab, setActiveTab] = useRecoilState<number>(
    myTimesheetTabsState(id),
  );

  const handleTabChange = (event: SyntheticEvent, newValue: number): void => {
    event.preventDefault();
    setActiveTab(newValue);
  };

  const MySheetOcTabs: FC = () => {
    const theme: Theme = useTheme();
    return (
      <Tabs
        variant="scrollable"
        scrollButtons="auto"
        value={activeTab}
        onChange={handleTabChange}
        aria-label="my sheet examiner dashboard tabs"
        sx={{
          backgroundColor: theme.palette.gray30.main,
        }}
      >
        {tabsDef.map((tabKey, index) => (
          <Tab
            id={String(index)}
            key={index}
            label={String(t(tabKey))}
            value={index}
            sx={{
              textTransform: "initial",
              fontSize: "16px",
              lineHeight: "20px",
              letterSpacing: "0.44px",
              height: "56px",
              minWidth: "72px",
              "&.Mui-selected": {
                backgroundColor: theme.palette.white.main,
                borderBottom: `4px solid ${theme.palette.primary.main}`,
                color: theme.palette.darkBase.main,
              },
              "&.MuiButtonBase-root": {
                padding: "0px 16px",
              },
              "@media (min-width:600px)": {
                minWidth: "72px",
              },
            }}
          />
        ))}
      </Tabs>
    );
  };

  return {
    activeTab,
    handleTabChange,
    MySheetOcTabs,
  };
};

export default useMyTimesheetTabsState;
